// src/quiz/gre-mock3/verbalData.js
// GRE Mock 3 — Verbal Reasoning (harder set).
// Section 1: questions 1–12 (18 min). Section 2: questions 13–27 (23 min).
// Question format: see ../greQuestionTypes.js

const SE_INSTRUCTION = "Select the two answer choices that, when used to complete the sentence, fit the meaning of the sentence as a whole and produce completed sentences that are alike in meaning.";

const BAUMOL_PASSAGE =
  "In the 1960s the economist William Baumol observed that productivity in some sectors of the economy rises far more slowly than in others. A string quartet requires the same four musicians and roughly the same time to perform a Beethoven quartet today as it did in 1800, whereas the labor required to manufacture a yard of cloth has fallen dramatically. Yet musicians' wages have not stagnated; to keep people from leaving the profession, orchestras must pay salaries that rise roughly in step with those in high-productivity industries. The result is that the relative cost of live performance—and, by extension, of labor-intensive services such as teaching and nursing—rises inexorably.\n\n" +
  "Baumol's analysis is sometimes invoked to excuse inefficiency, as though any rise in the cost of education or health care were simply the price of their resistance to automation. This reading misconstrues the argument. Baumol did not claim that such services cannot become more efficient, only that their productivity gains tend to be smaller, and he stressed that because the economy as a whole grows richer, society can continue to afford them even as their relative cost climbs. The policy lesson, on his account, is not resignation but the recognition that rising costs in these sectors need not signal mismanagement.";

const HADROSAUR_PASSAGE =
  "Paleontologists long interpreted the elaborate crests of certain hadrosaurs as snorkels or as chambers for storing air during dives. Both hypotheses foundered on anatomy: the crests lacked openings at the top, and their internal volume was trivial relative to the animals' lungs. A more recent proposal holds that the crests functioned as resonating chambers for producing low-frequency calls. Computer models of crest acoustics lend this idea some support, and the fact that crest shape differed markedly between species—and, within a species, between juveniles and adults—is consistent with a role in communication. Still, sound leaves no fossil trace, and the resonance hypothesis, while more plausible than its predecessors, remains an inference rather than a demonstration.";

const NOVEL_PASSAGE =
  "Literary historians have traditionally dated the emergence of the English novel to the early eighteenth century, crediting writers such as Defoe and Richardson with inventing a form defined by psychological realism and attention to ordinary life. This chronology has been challenged on two fronts. Some scholars point to earlier prose fictions—seventeenth-century romances and Elizabethan tales of urban life—that exhibit many supposedly novelistic features. Others question whether \"the novel\" names a coherent form at all, arguing that the category was constructed retrospectively by nineteenth-century critics who selected those predecessors that most resembled the fiction of their own day.\n\n" +
  "The two objections are not easily reconciled. The first assumes that the novel has stable defining features and merely disputes when they first appeared; the second denies that any such features exist independently of critics' preferences. If the second objection is correct, the first loses much of its force, since the search for an earlier origin presupposes the very coherence the second objection rejects. Yet the second objection has its own difficulty: in explaining why critics found certain earlier works congenial, it implicitly concedes that those works shared recognizable qualities, which is to grant the category a coherence it officially denies.";

const DESK_PASSAGE =
  "A survey found that people who regularly use standing desks report fewer back problems than people who use conventional desks. A furniture company concludes that switching office workers to standing desks would reduce the incidence of back problems among them.";

const MUSEUM_PASSAGE =
  "The museum's attendance rose by 15 percent in the year after it eliminated its admission fee. The museum's director argues that the fee had been deterring visitors who would otherwise have come, and that reinstating it would cause attendance to fall back to its earlier level.";

const verbalQuestions = [

  // ---------- SECTION 1 ----------

  {
    id: 1,
    question: "The critic's praise was so ______ that the playwright could not tell whether she had been complimented or subtly mocked.",
    options: ["equivocal", "effusive", "unstinting", "perfunctory", "belated"],
    answer: "equivocal",
    explanation: "Praise that could be read as either a compliment or mockery is open to two interpretations: equivocal. Effusive and unstinting praise would leave no doubt; perfunctory praise is careless but not ambiguous.",
  },
  {
    id: 2,
    type: "blanks",
    question: "The senator's reputation for (i)______ was hard to reconcile with her record: she had changed positions on nearly every major issue whenever the political winds (ii)______.",
    blanks: [
      { options: ["constancy", "opportunism", "eloquence"] },
      { options: ["shifted", "subsided", "intensified"] },
    ],
    answer: ["constancy", "shifted"],
    explanation: "A reputation that is \"hard to reconcile\" with frequent changes of position must be one for steadfastness: constancy. She changed positions whenever the political winds shifted. Opportunism would fit her record, so there would be nothing to reconcile.",
  },
  {
    id: 3,
    type: "blanks",
    question: "Although the study's authors take pains to (i)______ their conclusions, hedging nearly every claim, their critics charge that the data cannot support even such (ii)______ assertions; indeed, the sample is so small that any apparent pattern may be wholly (iii)______.",
    blanks: [
      { options: ["qualify", "trumpet", "conceal"] },
      { options: ["tentative", "sweeping", "novel"] },
      { options: ["spurious", "robust", "predictable"] },
    ],
    answer: ["qualify", "tentative", "spurious"],
    explanation: "\"Hedging nearly every claim\" defines (i): the authors qualify their conclusions. \"Even such\" refers back to those hedged claims, so they are tentative. A tiny sample means any pattern may be an artifact: spurious.",
  },
  {
    id: 4,
    type: "blanks",
    question: "Far from being a (i)______ of scientific progress, the controversy over the new vaccine (ii)______ it, compelling researchers to subject their methods to unusually rigorous scrutiny.",
    blanks: [
      { options: ["hindrance", "harbinger", "product"] },
      { options: ["spurred", "stymied", "mirrored"] },
    ],
    answer: ["hindrance", "spurred"],
    explanation: "Forcing more rigorous scrutiny advances science, so the controversy spurred progress. \"Far from\" sets up the opposite expectation: that controversy is a hindrance.",
  },
  {
    id: 5,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The mayor's response to the crisis was strikingly ______: rather than issuing grand pronouncements, she quietly arranged for shelters, supplies, and volunteers.",
    options: ["pragmatic", "grandiloquent", "practical", "histrionic", "dilatory", "sanguine"],
    answer: ["pragmatic", "practical"],
    explanation: "Quietly arranging concrete help instead of making speeches is a hands-on, results-oriented response: pragmatic and practical. Grandiloquent and histrionic describe the grand pronouncements she avoided.",
  },
  {
    id: 6,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "Long dismissed as a mere ______, the eighteenth-century diarist is now regarded by historians as an astute observer of the social upheavals of her time.",
    options: ["dilettante", "savant", "amateur", "polymath", "iconoclast", "sage"],
    answer: ["dilettante", "amateur"],
    explanation: "\"Mere\" signals a dismissive label, the opposite of \"astute observer\": dilettante and amateur. Savant, sage and polymath are compliments and would remove the contrast.",
  },
  {
    id: 7,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "Though the treaty was hailed at the time as a lasting settlement, it proved ______, collapsing within a decade amid renewed hostilities.",
    options: ["ephemeral", "enduring", "transient", "momentous", "contentious", "binding"],
    answer: ["ephemeral", "transient"],
    explanation: "A settlement that collapses within a decade, contrary to being \"lasting,\" is short-lived: ephemeral and transient. Contentious may be true but has no partner and does not capture the contrast with \"lasting.\"",
  },
  {
    id: 8,
    passage: BAUMOL_PASSAGE,
    question: "The primary purpose of the passage is to",
    options: [
      "explain an economic observation and correct a common misinterpretation of it",
      "argue that wages in high-productivity industries should be restrained",
      "demonstrate that education and health care cannot become more efficient",
      "criticize orchestras for paying salaries that exceed musicians' productivity",
      "propose policies for automating labor-intensive services",
    ],
    answer: "explain an economic observation and correct a common misinterpretation of it",
    explanation: "Paragraph 1 explains Baumol's observation; paragraph 2 says it \"is sometimes invoked to excuse inefficiency\" and that \"this reading misconstrues the argument.\" Choice C states the very misreading the author rejects.",
  },
  {
    id: 9,
    type: "multi",
    passage: BAUMOL_PASSAGE,
    question: "The passage implies which of the following about musicians' wages?",
    options: [
      "They have risen even though musicians' productivity has changed little.",
      "They are determined in part by competition with other industries for workers.",
      "They have risen faster than wages in manufacturing.",
    ],
    answer: [
      "They have risen even though musicians' productivity has changed little.",
      "They are determined in part by competition with other industries for workers.",
    ],
    explanation: "The quartet needs the same labor as in 1800, yet wages \"have not stagnated\" (A). Orchestras must pay more \"to keep people from leaving the profession\" (B). Wages rise \"roughly in step\" with other industries, not faster, so C is unsupported.",
  },
  {
    id: 10,
    passage: BAUMOL_PASSAGE,
    question: "According to the passage, those who invoke Baumol's analysis to excuse inefficiency err chiefly in assuming that",
    options: [
      "labor-intensive services are incapable of any productivity gains",
      "the economy as a whole is growing richer over time",
      "wages in labor-intensive sectors have remained stagnant",
      "live performance costs more than manufactured goods",
      "the relative cost of services will eventually decline",
    ],
    answer: "labor-intensive services are incapable of any productivity gains",
    explanation: "The author corrects them by noting that Baumol \"did not claim that such services cannot become more efficient, only that their productivity gains tend to be smaller.\" Their error is treating any cost rise as an unavoidable result of zero productivity growth.",
  },
  {
    id: 11,
    passage: HADROSAUR_PASSAGE,
    question: "The author mentions differences in crest shape between juveniles and adults primarily in order to",
    options: [
      "cite evidence that is compatible with the resonance hypothesis",
      "show that the snorkel hypothesis was partly correct",
      "suggest that juveniles were unable to produce calls",
      "argue that crests stored air only in adult animals",
      "explain why computer models of crest acoustics are unreliable",
    ],
    answer: "cite evidence that is compatible with the resonance hypothesis",
    explanation: "The differences are said to be \"consistent with a role in communication,\" i.e. compatible with the resonance hypothesis. Note the careful wording: consistent with, not proof of.",
  },
  {
    id: 12,
    passage: HADROSAUR_PASSAGE,
    question: "Which of the following best describes the author's assessment of the resonance hypothesis?",
    options: [
      "It is better supported than earlier explanations but cannot be conclusively confirmed.",
      "It has been established by acoustic modeling.",
      "It is no more plausible than the snorkel hypothesis.",
      "It is inconsistent with the anatomical evidence.",
      "It should be abandoned because sound leaves no fossil trace.",
    ],
    answer: "It is better supported than earlier explanations but cannot be conclusively confirmed.",
    explanation: "The final sentence calls it \"more plausible than its predecessors\" yet \"an inference rather than a demonstration.\" E overstates the point about fossil traces into a call for abandonment.",
  },

  // ---------- SECTION 2 ----------

  {
    id: 13,
    question: "The documentary's tone is relentlessly ______; even its account of the composer's late triumphs is shadowed by intimations of decline.",
    options: ["elegiac", "buoyant", "sardonic", "didactic", "whimsical"],
    answer: "elegiac",
    explanation: "A tone that casts even triumphs in the shadow of decline is mournful and wistful: elegiac. Sardonic means mocking, which nothing in the sentence suggests.",
  },
  {
    id: 14,
    question: "Scholars who expect the archive to settle the dispute will be disappointed: the letters are so ______ that they can be read as supporting either side.",
    options: ["oracular", "voluminous", "candid", "incendiary", "apocryphal"],
    answer: "oracular",
    explanation: "Letters that can be read as supporting either side are cryptic and ambiguous: oracular. Candid letters would settle the dispute; voluminous refers only to quantity.",
  },
  {
    id: 15,
    type: "blanks",
    question: "The novelist's prose, (i)______ in her early work, became increasingly spare over the decades, as though she had come to regard every superfluous word as a (ii)______.",
    blanks: [
      { options: ["ornate", "austere", "halting"] },
      { options: ["betrayal", "necessity", "triumph"] },
    ],
    answer: ["ornate", "betrayal"],
    explanation: "Prose that \"became increasingly spare\" must have begun as the opposite: ornate. Cutting every superfluous word suggests she saw such words as something to be rejected, a betrayal of her craft.",
  },
  {
    id: 16,
    type: "blanks",
    question: "The board's decision to (i)______ the program surprised observers, who had assumed that its (ii)______ results would ensure its survival; but the members evidently judged that its costs, rather than its achievements, were (iii)______.",
    blanks: [
      { options: ["terminate", "expand", "renew"] },
      { options: ["impressive", "dismal", "preliminary"] },
      { options: ["decisive", "negligible", "exaggerated"] },
    ],
    answer: ["terminate", "impressive", "decisive"],
    explanation: "Observers expected the results to \"ensure its survival,\" so the results were impressive and the surprising decision was to terminate it. The board must have considered the costs, not the achievements, decisive.",
  },
  {
    id: 17,
    type: "blanks",
    question: "Because the theory makes no predictions that could, even in principle, be contradicted by observation, its defenders' claim that it has never been (i)______ is less an endorsement than an (ii)______.",
    blanks: [
      { options: ["refuted", "articulated", "funded"] },
      { options: ["indictment", "accolade", "anachronism"] },
    ],
    answer: ["refuted", "indictment"],
    explanation: "A theory that cannot be contradicted by observation can never be refuted, so \"never refuted\" is not to its credit. It exposes the theory's untestability: an indictment rather than an endorsement.",
  },
  {
    id: 18,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The professor's lectures were notoriously ______, wandering from topic to topic without any discernible plan.",
    options: ["digressive", "rambling", "incisive", "pedantic", "terse", "soporific"],
    answer: ["digressive", "rambling"],
    explanation: "\"Wandering from topic to topic\" defines the blank: digressive and rambling. Soporific (sleep-inducing) might be true but is not what the sentence describes and has no partner.",
  },
  {
    id: 19,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "Despite the agency's assurances that the new regulations would be applied ______, inspectors were soon accused of targeting smaller firms while overlooking violations by larger ones.",
    options: ["evenhandedly", "equitably", "stringently", "sporadically", "leniently", "rigorously"],
    answer: ["evenhandedly", "equitably"],
    explanation: "The accusation is unequal treatment, so the assurance must have been of fair treatment: evenhandedly and equitably. Stringently and rigorously form a pair, but strictness is not what the accusation contradicts.",
  },
  {
    id: 20,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The memoir is less a candid self-examination than an exercise in ______, its author consistently casting himself as the hero of every episode.",
    options: ["self-aggrandizement", "self-effacement", "self-glorification", "self-reproach", "introspection", "reticence"],
    answer: ["self-aggrandizement", "self-glorification"],
    explanation: "Casting oneself as the hero of every episode is self-aggrandizement or self-glorification. Introspection is close to the \"candid self-examination\" the memoir is said not to be.",
  },
  {
    id: 21,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "Her rise in the company was ______: within five years she had advanced from junior analyst to chief financial officer.",
    options: ["meteoric", "gradual", "halting", "rapid", "fitful", "modest"],
    answer: ["meteoric", "rapid"],
    explanation: "Going from junior analyst to CFO in five years is a very fast rise: meteoric and rapid. Halting and fitful suggest stops and starts.",
  },
  {
    id: 22,
    passage: NOVEL_PASSAGE,
    question: "The primary purpose of the passage is to",
    options: [
      "analyze the relationship between two critiques of a conventional view and identify a weakness in one of them",
      "defend the traditional dating of the English novel against its critics",
      "argue that Elizabethan tales of urban life were the first English novels",
      "trace the influence of nineteenth-century critics on modern fiction",
      "reconcile two objections by showing that they rest on the same assumption",
    ],
    answer: "analyze the relationship between two critiques of a conventional view and identify a weakness in one of them",
    explanation: "Paragraph 2 explains how the objections relate (they \"are not easily reconciled\" and the second undercuts the first) and then exposes a difficulty in the second. E is the opposite: the author says they rest on contrary assumptions.",
  },
  {
    id: 23,
    passage: NOVEL_PASSAGE,
    question: "According to the passage, the first objection to the traditional chronology assumes that",
    options: [
      "the novel possesses defining features that can be identified in particular works",
      "the novel was invented by nineteenth-century critics",
      "Defoe and Richardson were influenced by seventeenth-century romances",
      "psychological realism is not a defining feature of the novel",
      "no prose fiction written before 1700 deserves serious study",
    ],
    answer: "the novel possesses defining features that can be identified in particular works",
    explanation: "\"The first assumes that the novel has stable defining features, and merely disputes when they first appeared.\"",
  },
  {
    id: 24,
    passage: NOVEL_PASSAGE,
    question: "The author suggests that the second objection is weakened by the fact that it",
    options: [
      "relies on an implicit acknowledgment that certain works share recognizable qualities",
      "ignores the evidence of seventeenth-century romances",
      "overestimates the originality of Defoe and Richardson",
      "accepts the traditional eighteenth-century chronology",
      "depends on the first objection's being correct",
    ],
    answer: "relies on an implicit acknowledgment that certain works share recognizable qualities",
    explanation: "To explain why critics found earlier works congenial, the objection \"implicitly concedes that those works shared recognizable qualities,\" granting the coherence it denies. This is a self-undermining argument.",
  },
  {
    id: 25,
    type: "multi",
    passage: NOVEL_PASSAGE,
    question: "It can be inferred that the author would agree with which of the following statements?",
    options: [
      "If the novel is merely a retrospective construction, debates about its earliest example lose much of their significance.",
      "The traditional eighteenth-century chronology has been definitively refuted.",
      "Neither objection to the traditional chronology faces any internal difficulty.",
    ],
    answer: [
      "If the novel is merely a retrospective construction, debates about its earliest example lose much of their significance.",
    ],
    explanation: "Only A: \"If the second objection is correct, the first loses much of its force.\" The author never says the chronology has been refuted (B) and explicitly identifies a difficulty in the second objection (C). Select-all questions can have just one correct answer.",
  },
  {
    id: 26,
    passage: DESK_PASSAGE,
    question: "Which of the following, if true, most seriously undermines the company's conclusion?",
    options: [
      "Many people who suffer from back problems find standing for long periods painful and therefore avoid standing desks.",
      "Standing desks are considerably more expensive than conventional desks.",
      "Some users of conventional desks report no back problems at all.",
      "The survey included office workers from several different industries.",
      "Standing desks were first introduced in the nineteenth century.",
    ],
    answer: "Many people who suffer from back problems find standing for long periods painful and therefore avoid standing desks.",
    explanation: "This suggests reverse causation: back problems lead people to avoid standing desks, so the correlation does not show that standing desks prevent back problems.",
  },
  {
    id: 27,
    passage: MUSEUM_PASSAGE,
    question: "The director's argument depends on the assumption that",
    options: [
      "the increase in attendance was not primarily due to some other change that occurred during the same year",
      "most visitors would be willing to pay a higher admission fee",
      "the museum's revenue increased after the fee was eliminated",
      "attendance at other museums declined during the same year",
      "the museum will open new exhibits in the coming year",
    ],
    answer: "the increase in attendance was not primarily due to some other change that occurred during the same year",
    explanation: "The director attributes the rise to removing the fee. If a blockbuster exhibit or other change caused it, the argument fails, so the argument must assume no such alternative cause (negation test).",
  },
];

export default verbalQuestions;
